import type { EnhancedToolResult, ToolDefinition, ToolExecutionContext } from '../../types';
import { VideoStateTool } from '../VideoStateTool';

import { ListBrandKitsTool } from './ListBrandKitsTool';

import { getBrandKitById } from 'js/src/data/brand-kits/brand-kits-selectors';
import { applyBrandKitOperation } from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

interface ApplyBrandKitArgs {
    brandKitId: number;
}

export class ApplyBrandKitTool extends VideoStateTool<ApplyBrandKitArgs> {
    public static readonly toolName = 'apply_brand_kit';

    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: ApplyBrandKitTool.toolName,
                description: `Apply a saved brand kit (its colors, fonts, and watermark) to the current video. Use ${ListBrandKitsTool.toolName} first to find the brand kit's id.`,
                parameters: {
                    type: 'object',
                    properties: {
                        brandKitId: {
                            type: 'integer',
                            description: 'The id of the brand kit to apply.',
                        },
                    },
                    required: ['brandKitId'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Applying brand kit';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public override async execute(
        args: ApplyBrandKitArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const { brandKitId } = args;

        const brandKit = getBrandKitById(this.getState(), brandKitId);
        if (!brandKit) {
            return {
                result: `Error: No brand kit found with id ${brandKitId}. Use ${ListBrandKitsTool.toolName} to see available brand kits.`,
                isSuccess: false,
            };
        }

        const { applyBrandKit } = makeOperationsCallable(
            { applyBrandKitOperation },
            this.luminary,
            this.dispatch,
            () => this.getState(),
        );

        applyBrandKit(brandKitId);

        return {
            result: `Successfully applied brand kit "${brandKit.get('name')}" to the video.`,
            isSuccess: true,
        };
    }
}
